import { MapPin, Clock, Phone, ExternalLink } from "lucide-react";

/**
 * Shared store branding block — reads a public store card object and renders logos,
 * details and ALL enabled service logos (with text fallback). Single source of truth so
 * Store Management edits flow everywhere. Never renders empty boxes or broken images.
 */
export function StoreBranding({ store, showName = true, showDirections = false, showInstructions = false, className = "" }) {
  if (!store) return null;
  const logos = [store.primary_logo, store.secondary_logo].filter(Boolean);
  const services = (store.services || store.featured_services || []).filter((x) => x && x.name);

  return (
    <div className={className} data-testid={`store-branding-${store.store_id || store.slug || "x"}`}>
      {logos.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="sb-logos">
          {logos.map((logo, i) => (
            <span key={i} className="inline-flex items-center justify-center rounded-lg bg-white/90 p-1.5">
              <img src={logo} alt={`${store.name || "Store"} logo`} className="h-8 w-auto max-w-[110px] object-contain" loading="lazy" />
            </span>
          ))}
        </div>
      )}

      {showName && store.name && <h3 className="mt-3 font-playfair text-lg text-white">{store.name}</h3>}

      <div className="mt-2 space-y-1.5 text-sm text-white/70">
        {store.full_address && <p className="flex items-start gap-2"><MapPin className="h-4 w-4 text-gold/70 mt-0.5 shrink-0" /> {store.full_address}</p>}
        {store.opening_hours_display?.length > 0 && <p className="flex items-start gap-2"><Clock className="h-4 w-4 text-gold/70 mt-0.5 shrink-0" /> {store.opening_hours_display.join(" · ")}</p>}
        {store.phone && <p className="flex items-start gap-2"><Phone className="h-4 w-4 text-gold/70 mt-0.5 shrink-0" /> {store.phone}</p>}
      </div>

      {showInstructions && store.collection_instructions && (
        <p className="mt-2 text-xs text-white/50">{store.collection_instructions}</p>
      )}

      {services.length > 0 && (
        <div className="mt-3" data-testid="sb-services">
          <p className="text-[11px] uppercase tracking-wide text-white/40 mb-2">Services here</p>
          <div className="flex flex-wrap items-center gap-2">
            {services.map((svc, i) => (
              svc.image ? (
                <span key={i} title={svc.name} className="inline-flex items-center justify-center rounded-md bg-white/90 p-1">
                  <img src={svc.image} alt={svc.name || "Service"} className="h-6 w-auto max-w-[72px] object-contain" loading="lazy" />
                </span>
              ) : (
                <span key={i} className="inline-flex items-center rounded-md border border-white/15 bg-white/5 px-2 py-1 text-[11px] text-white/70">{svc.name}</span>
              )
            ))}
          </div>
        </div>
      )}

      {showDirections && (store.google_maps_url || store.directions_url) && (
        <a href={store.google_maps_url || store.directions_url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-gold hover:opacity-90" data-testid="sb-directions">
          Get directions <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}
    </div>
  );
}

export default StoreBranding;
